import React, { useCallback, useContext, useEffect, useState } from 'react';
import _ from 'lodash';
import { Box, Loader } from '@wix/design-system';
import { INTERACTIONS, MORE_APPS_TAB_ID } from '../constants/constants';
import type { RegularAppData } from '../services/appMarket/appMarket.types';
import { AUTOMATION_IDS } from '../constants/automationIds';
import { BI_EVENTS_IDS } from '../constants/biEvents';
import { prepareSegmentsAppsData } from '../services/dealer/dealer.segmentApps';
import { AppManagerDataContext } from '../providers/AppManagerDataProvider';
import { EditorPlatformFedopsContext } from '../../providers/EditorPlatformFedopsProvider';
import { useBILogger } from '@wix/fe-essentials/react';
import { MyBusiness } from './MyBusiness/MyBusiness';
import styles from './AppManagerContainer.module.scss';
import { AppManagerOwnProps } from '../../../types/appManager';
import { getBIReportData } from '../utils/biEvents';

export const AppManagerContainer: React.FC<AppManagerOwnProps> = ({
  selectedAppId,
  selectedAppFromUrl,
  origin,
}) => {
  const { api, data } = useContext(AppManagerDataContext);
  const { fedopsLogger } = useContext(EditorPlatformFedopsContext);
  const logger = useBILogger();

  const [isLoadingAppManifests, setIsLoadingAppManifests] =
    useState<boolean>(true);

  const {
    segmentApps: [
      segmentApps,
      { isLoading: isLoadingSegmentApps, error: errorFromLoadSegmentApps },
    ],
    dealerData: [
      dealerData,
      { isLoading: isLoadingDealer, error: errorFromLoadDealerData },
    ],
    segmentContent: [
      segmentContent,
      { isLoading: isLoadingSegmentContent, error: errorFromSegmentContent },
    ],
    marketApps: [
      marketApps,
      { isLoading: isLoadingAllApps, error: errorFromLoadAllApps },
    ],
  } = data;

  const getAvailableApps = useCallback((): RegularAppData[] => {
    const availableApps = (marketApps && api.normalizeApps(marketApps)) || [];
    const hasSegmentApps = segmentApps && segmentApps.length > 0;

    if (hasSegmentApps) {
      return prepareSegmentsAppsData(availableApps, segmentApps);
    }

    return availableApps;
  }, [api, marketApps, segmentApps]);

  const availableApps = getAvailableApps();

  const [installedOrPendingApps, moreApps] = _.partition(
    availableApps,
    (app) => app.isInstalled || app.isPending,
  );

  const error =
    errorFromLoadDealerData ||
    errorFromLoadSegmentApps ||
    errorFromSegmentContent ||
    errorFromLoadAllApps;

  if (error) {
    throw error;
  }

  const isLoadingData =
    isLoadingDealer ||
    isLoadingSegmentApps ||
    isLoadingSegmentContent ||
    isLoadingAppManifests ||
    isLoadingAllApps;

  useEffect(() => {
    fedopsLogger.appLoaded();

    const appsCompleted = api.checkAppsCompletion(() => {
      setIsLoadingAppManifests(false);
    });

    if (appsCompleted) {
      setIsLoadingAppManifests(false);
    }
  }, []);

  useEffect(() => {
    if (isLoadingData) {
      fedopsLogger.interactionStarted(INTERACTIONS.APP_MANAGER.LOAD);
      return;
    }

    fedopsLogger.interactionEnded(INTERACTIONS.APP_MANAGER.LOAD);
    const initialApp =
      availableApps?.find(
        (app) => app.id === selectedAppId || app.slug === selectedAppFromUrl,
      )?.id ||
      installedOrPendingApps[0]?.id ||
      MORE_APPS_TAB_ID;

    logger.log(
      getBIReportData(BI_EVENTS_IDS.PANEL_LOADED, initialApp, {
        origin,
        tabs_info: installedOrPendingApps.map((app) => app.id),
      }),
    );
  }, [isLoadingData]);

  if (isLoadingData) {
    return (
      <Box width="450px" height="100%" align="center" verticalAlign="middle">
        <Loader />
      </Box>
    );
  }

  return (
    <div className={styles.content} data-aid={AUTOMATION_IDS.PANEL_CONTENT}>
      <MyBusiness
        moreApps={moreApps}
        apps={installedOrPendingApps}
        initialSelectedAppId={selectedAppId}
        segmentContent={segmentContent}
        dealerData={dealerData}
        origin={origin}
      />
    </div>
  );
};
